import axios from "axios";
import { toast } from "react-toastify";
import i18n from "../../i18n";
import { serverErrorText } from "../../utils/serverErrorText";
import { mergedLinksFromHeaders } from "../import-warnings/notifyMergedLinks";
import { isGanttPerfDebugEnabled } from "../../utils/ganttPerfDebug";
import { addMissingDependenciesToGantt } from "../../utils/ganttDependencies";

import {
  ADD_SCHEDULE_ACTIVITY_REQUEST,
  ADD_SCHEDULE_ACTIVITY_SUCCESS,
  ADD_SCHEDULE_ACTIVITY_ERROR,
  UPDATE_SCHEDULE_ACTIVITIES_REQUEST,
  UPDATE_SCHEDULE_ACTIVITIES_SUCCESS,
  UPDATE_SCHEDULE_ACTIVITIES_ERROR,
  DELETE_SCHEDULE_ACTIVITY,
  DELETE_SCHEDULE_ACTIVITIES,
  GET_SCHEDULE_ACTIVITIES_ERROR,
  GET_SCHEDULE_ACTIVITIES_REQUEST,
  GET_SCHEDULE_ACTIVITIES_SUCCESS,
  UPDATE_SCHEDULE_ACTIVITIES_WITH_USAGE_REQUEST,
  UPDATE_SCHEDULE_ACTIVITIES_WITH_USAGE_SUCCESS,
  UPDATE_SCHEDULE_ACTIVITIES_WITH_USAGE_ERROR,
  UPDATE_DETAIL_ACTIVITY_REQUEST,
  UPDATE_DETAIL_ACTIVITY_SUCCESS,
  UPDATE_DETAIL_ACTIVITY_ERROR,
  CLEAR_SCHEDULE_ACTIVITIES,
  GET_REGULARS_ERROR,
  GET_REGULARS_CHUNK,
  SET_REGULARS_SCOPE,
  LOOK_AHEAD_SA_LOAD_FAILED,
  MLAP_LOAD_FAILED,
  UPDATE_SCHEDULE_ACTIVITIES_IMPORT_SCOPE_REQUEST,
  UPDATE_SCHEDULE_ACTIVITIES_IMPORT_SCOPE_SUCCESS,
  UPDATE_SCHEDULE_ACTIVITIES_IMPORT_SCOPE_ERROR,
  UPDATE_REGULAR,
  UPLOAD_MPP_REQUEST,
  UPLOAD_MPP_SUCCESS,
  UPLOAD_MPP_ERROR,
  UPDATE_SCHEDULE_ACTIVITIES_BY_IDS,
  RESET_BASELINE_ON_ACTIVITY,
  RESET_BASELINE_ON_SUMMARY,
  ADD_SUBTASK_SUCCESS,
  ADD_SUMMARY_SUCCESS,
  DUPLICATE_ACTIVITY_SUCCESS,
  GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMNS_REQUEST,
  GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMNS_SUCCESS,
  GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMNS_ERROR,
  GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMN_NAMES_REQUEST,
  GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMN_NAMES_SUCCESS,
  GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMN_NAMES_ERROR,
  ADD_SCHEDULE_ACTIVITIES,
  UPDATE_SCHEDULE_ACTIVITY_FINISH_DATE_WITH_DURATION_SUCCESS,
  UPDATE_SCHEDULE_ACTIVITY_CUSTOM_COLUMN,
  SHOW_MASK_ON_GANTT,
} from "../types";

import { PDA_PREFIX } from "../../global/constants";
import { awaitAsyncJob } from "../ws/awaitJobOutcome";
import {
  abortOnDestroy,
  followScheduleJob,
  putBlockSchedule,
  submitWhenFree,
  currentUsername,
  regenerationErrorMessage,
} from "./blockScheduleRun";
import {
  registerScheduleFollow,
  unregisterScheduleFollow,
} from "./scheduleFollowRegistry";
import { startRegenerationCard } from "./regenerationCard";
import { logout } from "../security-services/SecurityActions";
import logoutforSpecificErrorCodes from "../../securityUtils/LogoutUtils";
import { setUpdatingStatus } from "../utils-services/utilsActions";
import {
  getResourcesByScenarioId,
  simulationError,
  simulationProcess,
  simulationSuccess,
  updateSimulationUsage,
} from "../resource-services/resourceActions";
import { fetchGetScenario } from "../scenario-services/scenarioActions";
import { topDownResourceTaskUpdate } from "../scheduler-services/schedulerActions";
import { realignSequenceOnGantt } from "../../utils/ScheduleActivityUtils/ScheduleActivityUtils";
import {
  windowInsideCoverage,
  windowsOverlap,
  missingBands,
  mergeCoverage,
} from "../../utils/periodWindow/periodWindow";
import { armRevealIntent } from "../../components/Bryntum/Gantt/reveal/revealIntent";
import { emitPenaltiesChanged } from "../quantity-services/penaltyLossService";

// actions

const getScheduleActivitiesRequest = () => ({
  type: GET_SCHEDULE_ACTIVITIES_REQUEST,
});

const getScheduleActivitiesSuccess = (scheduleActivities, meta) => ({
  type: GET_SCHEDULE_ACTIVITIES_SUCCESS,
  payload: scheduleActivities,
  meta,
});
const getScheduleActivitiesError = (err) => ({
  type: GET_SCHEDULE_ACTIVITIES_ERROR,
  payload: err,
});

const addScheduleActivityRequest = () => ({
  type: ADD_SCHEDULE_ACTIVITY_REQUEST,
});

const addScheduleActivitySuccess = (scheduleActivity, reorderData = null) => ({
  type: ADD_SCHEDULE_ACTIVITY_SUCCESS,
  payload: { scheduleActivity, reorderData },
});
const addScheduleActivityError = (err) => ({
  type: ADD_SCHEDULE_ACTIVITY_ERROR,
  payload: err,
});

const getRegularsError = (err) => ({
  type: GET_REGULARS_ERROR,
  payload: err,
});

// Rows per look-ahead chunk. ~1000 rows load in ~1-2s, comfortably under the
// reverse-proxy read timeout, so each batch stays fast even as data grows.
const LOOK_AHEAD_CHUNK_SIZE = 1000;

// Stable cache key for a look-ahead request: same period + same filters => same key.
export const lookAheadCacheKey = (dateDTO = {}) => {
  const projects = [...(dateDTO.projectNames || [])].sort().join(",");
  const resources = [...(dateDTO.resourceIds || [])].sort().join(",");
  return `${dateDTO.start}|${dateDTO.finish}|${projects}|${resources}`;
};

// Tracks the latest look-ahead request so in-flight chunks from a superseded period
// (the user changed period/filters mid-stream) are dropped instead of appending stale rows.
let activeLookAheadKey = null;

// Cap on simultaneous chunk requests. A wide period (e.g. one year) can produce dozens of
// chunks; firing them all at once stampedes the backend (thread/connection-pool exhaustion)
// and 504s the whole page. A small pool keeps the load steady while still streaming.
const LOOK_AHEAD_CHUNK_CONCURRENCY = 4;

// Hard cap on rows loaded into the page for one period: the stream STOPS at the cap and
// tells the user to narrow the period or filters, instead of letting the page die trying
// to swallow everything (an unbounded one-year window froze it). 10k is a deliberate
// ceiling above the known-comfortable ~2-3k — to re-measure against real volumes once the
// large-set fixes are deployed, and lower/raise in one line if the live behavior says so.
export const MAX_LOADED_ROWS = 10000;

// One toast per capped load, not one per capped stream (SA + RT both hit the cap together).
let rowLimitToastShownAt = 0;
export const warnRowLimitReached = (total) => {
  const now = Date.now();
  if (now - rowLimitToastShownAt < 5000) return;
  rowLimitToastShownAt = now;
  toast.warn(
    i18n.t("taskFocus.rowLimitReached", {
      max: MAX_LOADED_ROWS,
      total,
      defaultValue:
        "Only the first {{max}} rows of {{total}} were loaded — the page cannot handle the whole period. Narrow the period or the filters to see everything.",
    }),
    { autoClose: 12000 },
  );
};

// Runs async task factories with a bounded number in flight at a time, preserving order.
const runWithConcurrency = async (taskFactories, limit) => {
  let next = 0;
  const runners = Array.from(
    { length: Math.min(limit, taskFactories.length) },
    async () => {
      while (next < taskFactories.length) {
        const current = next;
        next += 1;
        await taskFactories[current]();
      }
    },
  );
  await Promise.all(runners);
};

const updateScheduleActivitiesRequest = () => ({
  type: UPDATE_SCHEDULE_ACTIVITIES_REQUEST,
});

export const updateScheduleActivitiesSuccess = (scheduleActivities) => ({
  type: UPDATE_SCHEDULE_ACTIVITIES_SUCCESS,
  payload: scheduleActivities,
});
const updateScheduleActivitiesError = (err) => ({
  type: UPDATE_SCHEDULE_ACTIVITIES_ERROR,
  payload: err,
});

const updateScheduleActivitiesImportScopeRequest = () => ({
  type: UPDATE_SCHEDULE_ACTIVITIES_IMPORT_SCOPE_REQUEST,
});

const updateScheduleActivitiesImportScopeSuccess = (scheduleActivities) => ({
  type: UPDATE_SCHEDULE_ACTIVITIES_IMPORT_SCOPE_SUCCESS,
  payload: scheduleActivities,
});

const updateScheduleActivitiesImportScopeError = (err) => ({
  type: UPDATE_SCHEDULE_ACTIVITIES_IMPORT_SCOPE_ERROR,
  payload: err,
});

const deleteScheduleActivitySuccess = (id) => ({
  type: DELETE_SCHEDULE_ACTIVITY,
  payload: id,
});

const deleteScheduleActivitiesSuccess = (ids) => ({
  type: DELETE_SCHEDULE_ACTIVITIES,
  payload: ids,
});

const updateDetailActivityRequest = () => ({
  type: UPDATE_DETAIL_ACTIVITY_REQUEST,
});

const updateDetailActivitySuccess = (scheduleActivity) => ({
  type: UPDATE_DETAIL_ACTIVITY_SUCCESS,
  payload: scheduleActivity,
});
const updateDetailActivityError = (err) => ({
  type: UPDATE_DETAIL_ACTIVITY_ERROR,
  payload: err,
});

const updateScheduleActivitiesWithUsageRequest = () => ({
  type: UPDATE_SCHEDULE_ACTIVITIES_WITH_USAGE_REQUEST,
});
const updateScheduleActivitiesWithUsageSuccess = (payload) => ({
  type: UPDATE_SCHEDULE_ACTIVITIES_WITH_USAGE_SUCCESS,
  payload,
});
const updateScheduleActivitiesWithUsageError = (error) => ({
  type: UPDATE_SCHEDULE_ACTIVITIES_WITH_USAGE_ERROR,
  payload: error,
});

export const updateRegular = (data) => ({
  type: UPDATE_REGULAR,
  payload: data,
});

const uploadMppRequest = () => ({
  type: UPLOAD_MPP_REQUEST,
});

const uploadMppSuccess = (data) => ({
  type: UPLOAD_MPP_SUCCESS,
  payload: data,
});

const uploadMppError = (err) => ({
  type: UPLOAD_MPP_ERROR,
  payload: err,
});

const updateScheduleActivitiesByIds = (data, requestId) => ({
  type: UPDATE_SCHEDULE_ACTIVITIES_BY_IDS,
  payload: data,
  meta: { requestId },
});

const resetBaselineOnActivity = (activity) => ({
  type: RESET_BASELINE_ON_ACTIVITY,
  payload: activity,
});

const resetBaselineOnSummary = (activity) => ({
  type: RESET_BASELINE_ON_SUMMARY,
  payload: activity,
});

const addSubtaskSuccess = (data) => ({
  type: ADD_SUBTASK_SUCCESS,
  payload: data,
});

const addSummarySuccess = (data) => ({
  type: ADD_SUMMARY_SUCCESS,
  payload: data,
});

const duplicateActivitySuccess = (data) => ({
  type: DUPLICATE_ACTIVITY_SUCCESS,
  payload: data,
});

const getScheduleActivityCustomColumnsRequest = () => ({
  type: GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMNS_REQUEST,
});

const getScheduleActivityCustomColumnsSuccess = (columns) => ({
  type: GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMNS_SUCCESS,
  payload: columns,
});

const getScheduleActivityCustomColumnsError = (error) => ({
  type: GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMNS_ERROR,
  payload: error,
});

const getScheduleActivityCustomColumnNamesRequest = () => ({
  type: GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMN_NAMES_REQUEST,
});

const getScheduleActivityCustomColumnNamesSuccess = (columns) => ({
  type: GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMN_NAMES_SUCCESS,
  payload: columns,
});

const getScheduleActivityCustomColumnNamesError = (error) => ({
  type: GET_SCHEDULE_ACTIVITY_CUSTOM_COLUMN_NAMES_ERROR,
  payload: error,
});

const updateScheduleActivityFinishDateWithDurationSuccess = (data) => ({
  type: UPDATE_SCHEDULE_ACTIVITY_FINISH_DATE_WITH_DURATION_SUCCESS,
  payload: data,
});

///  Fetch

// These schedule fetches carry no AbortController, and a regeneration fires a
// BURST of them (one per scenario completing over WS, plus the direct one), so
// their responses can settle out of order: a slow chunked fetch used to land
// its full dataset AFTER a newer one and put the pre-regeneration rows back
// into the store, where they stayed until the user hit Refresh. Each call
// claims a sequence number for its scenario and only dispatches while it is
// still the most recent claim.
const latestScheduleRequest = new Map();

// Keyed by String: callers hand this both route/query-string ids and numeric
// ones, and "15196" claiming a different sequence than 15196 lets an older
// request dispatch over a newer one.
const claimScheduleRequest = (scenarioId) => {
  const key = String(scenarioId);
  const requestId = (latestScheduleRequest.get(key) ?? 0) + 1;
  latestScheduleRequest.set(key, requestId);
  return {
    isLatest: () => latestScheduleRequest.get(key) === requestId,
    requestId,
  };
};

// The sequence a by-ids refresh rides on. It does NOT claim a new number: a
// by-ids merge must not invalidate a chunked full load in flight, or an edit
// during a large scenario's initial load would leave the planning half
// rendered. It only stamps the rows it refreshed, so the reducer can tell a
// full payload that was requested EARLIER not to overwrite them.
export const currentScheduleSequence = (scenarioId) =>
  latestScheduleRequest.get(String(scenarioId)) ?? 0;

// By-ids refreshes need their own ordering, separate from the full-fetch
// sequence above. Two of them can be in flight at once — one per edit — and they
// carry no ordering of their own, so the slower one used to land its older
// snapshot over the newer one's rows. Stamping alone does not help: both ride
// the same full-fetch sequence, so the reducer cannot tell them apart.
const latestByIdsRequest = new Map();

const claimByIdsRequest = (scenarioId) => {
  const key = String(scenarioId);
  const requestId = (latestByIdsRequest.get(key) ?? 0) + 1;
  latestByIdsRequest.set(key, requestId);
  return () => latestByIdsRequest.get(key) === requestId;
};

// Cap on simultaneous schedule chunk requests, for the same reason as
// LOOK_AHEAD_CHUNK_CONCURRENCY: a large scenario produces dozens of chunks, and
// a regeneration fires one fetch per scenario on top of that. Firing every chunk
// at once stampedes the backend (thread/connection-pool exhaustion) and 504s the
// whole page instead of streaming it in.
const SCHEDULE_CHUNK_CONCURRENCY = 4;

// Fetches chunks 2..totalChunks through a bounded pool and appends them to
// `into` in chunk order — each runner fills its own slot, so completing out of
// order cannot interleave rows. `shouldContinue` stops the pool mid-stream when
// a newer fetch has superseded this one: the caller drops the partial result
// anyway, and a burst of superseded fetches is exactly what saturates the
// backend.
const appendRemainingScheduleChunks = async (
  scenarioId,
  { totalChunks, chunkSize },
  into,
  shouldContinue = () => true,
) => {
  const chunkRows = [];
  const factories = Array.from(
    { length: totalChunks - 1 },
    (_, index) => async () => {
      if (!shouldContinue()) return;
      const chunk = index + 2;
      const chunkRes = await axios.get(
        `${PDA_PREFIX}/scenario/${scenarioId}/schedule?chunk=${chunk}&chunkSize=${chunkSize}`,
      );
      chunkRows[index] = chunkRes.data?.data || chunkRes.data || [];
    },
  );
  await runWithConcurrency(factories, SCHEDULE_CHUNK_CONCURRENCY);
  chunkRows.forEach((rows) => into.push(...rows));
  return into;
};

// A 204, an empty body or an error page used to be coerced to `[]` and
// dispatched as a success. Downstream, the Gantt's incremental sync reads an
// empty payload as "every activity was deleted" — that is how a whole planning
// vanished right after an "update usage", with nothing in the console. Throwing
// routes it to the caller's catch, which surfaces a real error and leaves the
// rows in place. An empty ARRAY is still a valid answer and passes through.
const readScheduleActivities = (payload, scenarioId) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  throw new Error(
    `Unexpected schedule response shape for scenario ${scenarioId}`,
  );
};

export const fetchGetScheduleActivities = (scenarioId) => async (dispatch) => {
  const { isLatest: isLatestRequest, requestId } =
    claimScheduleRequest(scenarioId);
  dispatch(getScheduleActivitiesRequest(scenarioId));
  const _t0 = performance.now();
  if (isGanttPerfDebugEnabled()) {
    // eslint-disable-next-line no-console
    console.info(
      `[GANTT-PERF DEBUG http] fetchSchedule.start scenario=${scenarioId}`,
    );
  }
  try {
    const res = await axios.get(
      `${PDA_PREFIX}/scenario/${scenarioId}/schedule`,
    );
    const _t1 = performance.now();
    if (isGanttPerfDebugEnabled()) {
      // eslint-disable-next-line no-console
      console.info(
        `[GANTT-PERF DEBUG http] fetchSchedule.xhr scenario=${scenarioId} ` +
          `xhrMs=${(_t1 - _t0).toFixed(0)} ` +
          `chunked=${res.data?.meta?.totalChunks > 1} ` +
          `firstChunkRows=${
            res.data?.meta?.totalChunks > 1
              ? res.data?.data?.length || 0
              : Array.isArray(res.data)
                ? res.data.length
                : 0
          }`,
      );
    }

    // Support chunked API response: if the backend returns { data, meta } with
    // totalChunks > 1, fetch remaining chunks and concatenate all activities.
    // Falls back seamlessly to the current monolithic response (plain array).
    if (res.data?.meta?.totalChunks > 1) {
      const allActivities = [...res.data.data];
      const { totalChunks } = res.data.meta;
      const chunkSize = res.data.meta.chunkSize;

      // Dispatch first chunk immediately so the UI can start rendering
      if (!isLatestRequest()) return;
      dispatch(
        getScheduleActivitiesSuccess(allActivities, {
          isPartial: true,
          requestId,
        }),
      );
      if (isGanttPerfDebugEnabled()) {
        // eslint-disable-next-line no-console
        console.info(
          `[GANTT-PERF DEBUG http] fetchSchedule.dispatch.firstChunk scenario=${scenarioId} ` +
            `rows=${allActivities.length} sinceStartMs=${(performance.now() - _t0).toFixed(0)}`,
        );
      }

      // Fetch the remaining chunks through a bounded pool
      await appendRemainingScheduleChunks(
        scenarioId,
        { totalChunks, chunkSize },
        allActivities,
        isLatestRequest,
      );
      // Dispatch full dataset
      if (!isLatestRequest()) return;
      dispatch(getScheduleActivitiesSuccess(allActivities, { requestId }));
      if (isGanttPerfDebugEnabled()) {
        // eslint-disable-next-line no-console
        console.info(
          `[GANTT-PERF DEBUG http] fetchSchedule.dispatch.full scenario=${scenarioId} ` +
            `rows=${allActivities.length} chunks=${totalChunks} ` +
            `totalMs=${(performance.now() - _t0).toFixed(0)}`,
        );
      }
    } else {
      const activities = readScheduleActivities(res.data, scenarioId);
      if (!isLatestRequest()) return;
      dispatch(getScheduleActivitiesSuccess(activities, { requestId }));
      if (isGanttPerfDebugEnabled()) {
        // eslint-disable-next-line no-console
        console.info(
          `[GANTT-PERF DEBUG http] fetchSchedule.dispatch.full scenario=${scenarioId} ` +
            `rows=${Array.isArray(res.data) ? res.data.length : 0} chunks=1 ` +
            `totalMs=${(performance.now() - _t0).toFixed(0)}`,
        );
      }
    }
  } catch (err) {
    // Bail before any side effect: a superseded fetch must not clear
    // `isFetching`, raise an error over the newer one still running, toast at
    // the user, or — on a 401 — log them out of a session the newer request is
    // using perfectly well.
    if (!isLatestRequest()) return;
    toast.error("Impossible to fetch activities");
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch(getScheduleActivitiesError(err.message));
  }
};

/**
 * Reload the Gantt after a job that replaced the scenario's schedule wholesale
 * (scope import, structure regeneration).
 *
 * A plain refetch reaches a mounted Gantt through the incremental diff, which
 * refuses a payload that swaps existing rows for rows it cannot date
 * (updateGanttData) — exactly what a manually created project's PSM / PFM /
 * summary rows next to freshly generated activities look like. The Gantt then
 * stayed empty until a page refresh (RATIER FIGEAC C-21). The mask takes the
 * full-load branch instead (shouldLoadFullDataset), the way a project switch
 * does, and GET_SCHEDULE_ACTIVITIES_SUCCESS / _ERROR lower it again.
 */
export const reloadGanttAfterImport = (dispatch, scenarioId) => {
  dispatch({ type: SHOW_MASK_ON_GANTT });
  return dispatch(fetchGetScheduleActivities(scenarioId));
};

/**
 * Normalises the schedule endpoint's two response shapes into a flat activity array: a plain array,
 * or the chunked `{ data, meta: { totalChunks, chunkSize } }` wrapper whose remaining chunks are
 * fetched here.
 */
const fetchAllScheduleChunks = async (
  scenarioId,
  firstResponse,
  shouldContinue,
) => {
  const payload = firstResponse?.data;
  if (!(payload?.meta?.totalChunks > 1)) {
    return readScheduleActivities(payload, scenarioId);
  }

  const { totalChunks, chunkSize } = payload.meta;
  return appendRemainingScheduleChunks(
    scenarioId,
    { totalChunks, chunkSize },
    [...payload.data],
    shouldContinue,
  );
};

export const refetchGetScheduleActivities =
  (scenarioId) => async (dispatch) => {
    const { isLatest: isLatestRequest, requestId } =
      claimScheduleRequest(scenarioId);
    const _t0 = performance.now();
    if (isGanttPerfDebugEnabled()) {
      // eslint-disable-next-line no-console
      console.info(
        `[GANTT-PERF DEBUG http] refetchSchedule.start scenario=${scenarioId}`,
      );
    }
    try {
      const res = await axios.get(
        `${PDA_PREFIX}/scenario/${scenarioId}/schedule`,
      );
      // The chunked shape ({ data, meta }) has to be unwrapped here too. Handing the reducer the
      // wrapper made it throw on payload.map, and the bare catch below swallowed it — leaving the
      // Gantt showing the pre-simulation rows and every stale arrow with them.
      const activities = await fetchAllScheduleChunks(
        scenarioId,
        res,
        isLatestRequest,
      );
      if (!isLatestRequest()) return;
      dispatch(getScheduleActivitiesSuccess(activities, { requestId }));
      if (isGanttPerfDebugEnabled()) {
        // eslint-disable-next-line no-console
        console.info(
          `[GANTT-PERF DEBUG http] refetchSchedule.dispatch scenario=${scenarioId} ` +
            `rows=${activities.length} ` +
            `totalMs=${(performance.now() - _t0).toFixed(0)}`,
        );
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[refetchGetScheduleActivities] failed", err);
      // Only logging leaves `isFetching` true and `items` empty forever whenever
      // this refetch superseded an initial fetch that had already dispatched its
      // REQUEST — a WS "update usage" landing on a scenario still loading its
      // chunks. The superseded fetch is guarded and dispatches nothing, so this
      // is the only remaining chance to release the spinner.
      if (isLatestRequest()) dispatch(getScheduleActivitiesError(err.message));
    }
  };

const LOOK_AHEAD_URL = `${PDA_PREFIX}/schedule-activity/current-user/extended-regulars-with-conflicts`;

const fetchLookAheadChunk = (dateDTO, chunk, chunkSize, band = false) =>
  axios.post(
    `${LOOK_AHEAD_URL}?chunk=${chunk}&chunkSize=${chunkSize}${band ? "&band=true" : ""}`,
    dateDTO,
  );

// Filters-only part of the cache key: coverage is only reusable while the non-date filters
// (projects/resources) are identical.
export const lookAheadFilterKey = (dateDTO = {}) => {
  const projects = [...(dateDTO.projectNames || [])].sort().join(",");
  const resources = [...(dateDTO.resourceIds || [])].sort().join(",");
  return `${projects}|${resources}`;
};

// Streams one chunked load (a full window or a delta band) into the pool.
// The backend's interval predicate is an INCLUSIVE OVERLAP for every load (MLAP-FILTER
// DEC-1); `band=true` is kept for the delta-band callers (same predicate server-side).
const streamLookAheadChunks = async (
  dispatch,
  dateDTO,
  key,
  { band = false, replaceFirst = false } = {},
) => {
  const firstRes = await fetchLookAheadChunk(
    dateDTO,
    1,
    LOOK_AHEAD_CHUNK_SIZE,
    band,
  );
  // A newer period/filter load started while this was in flight -> drop these results.
  if (activeLookAheadKey !== key) return;

  // 204 No Content -> no rows for this window/band.
  if (firstRes.status === 204 || !firstRes.data) {
    dispatch({
      type: GET_REGULARS_CHUNK,
      payload: { rows: [], replace: replaceFirst, complete: true, key },
    });
    return;
  }

  const meta = firstRes.data.meta;
  if (meta && typeof meta.totalChunks === "number") {
    const chunkSize = meta.chunkSize || LOOK_AHEAD_CHUNK_SIZE;
    // Row-limit guard: stop issuing chunks once the cap is reached and tell the user,
    // instead of loading a volume the page cannot survive.
    const maxChunks = Math.max(1, Math.floor(MAX_LOADED_ROWS / chunkSize));
    const totalChunks = Math.min(meta.totalChunks, maxChunks);
    const capped = meta.totalChunks > totalChunks;
    if (capped) warnRowLimitReached(meta.totalRows);

    dispatch({
      type: GET_REGULARS_CHUNK,
      payload: {
        rows: firstRes.data.data || [],
        replace: replaceFirst,
        complete: totalChunks <= 1,
        key,
        // Feeds the non-blocking "X / Y rows" streaming chip. Band loads add to what's
        // already pooled (the reducer offsets by the pool size at dispatch time).
        // When capped, the chip target is what will actually be loaded.
        totalRows: capped ? totalChunks * chunkSize : meta.totalRows,
        band,
        incompleteDates: meta.incompleteDates,
      },
    });

    if (totalChunks > 1) {
      let remaining = totalChunks - 1;
      const taskFactories = [];
      for (let chunk = 2; chunk <= totalChunks; chunk++) {
        const chunkIndex = chunk;
        taskFactories.push(async () => {
          // Stop issuing further requests once the load is superseded.
          if (activeLookAheadKey !== key) {
            remaining -= 1;
            return;
          }
          try {
            const res = await fetchLookAheadChunk(
              dateDTO,
              chunkIndex,
              chunkSize,
              band,
            );
            remaining -= 1;
            if (activeLookAheadKey !== key) return;
            dispatch({
              type: GET_REGULARS_CHUNK,
              payload: {
                rows: (res.data && res.data.data) || [],
                replace: false,
                complete: remaining === 0,
                key,
              },
            });
          } catch (e) {
            remaining -= 1;
            if (activeLookAheadKey === key) {
              // A lost chunk is a partial payload (L7): settle the stream when it was the last
              // one, and flag the load as failed so the pane shows a refusal (DEC-5).
              dispatch(lookAheadSaLoadFailed(e));
              if (remaining === 0) {
                dispatch({
                  type: GET_REGULARS_CHUNK,
                  payload: { rows: [], replace: false, complete: true, key },
                });
              }
            }
          }
        });
      }
      // Bounded concurrency: stream the remaining chunks a few at a time, not all at once.
      await runWithConcurrency(taskFactories, LOOK_AHEAD_CHUNK_CONCURRENCY);
    }
  } else {
    // Legacy: backend returned the full array (no pagination).
    const rows = Array.isArray(firstRes.data)
      ? firstRes.data
      : firstRes.data.data || [];
    dispatch({
      type: GET_REGULARS_CHUNK,
      payload: { rows, replace: replaceFirst, complete: true, key },
    });
  }
};

/**
 * Delta-band period loading for the look-ahead grid.
 *
 * - NARROW (new window inside the loaded coverage): no network at all — the reducer's pool
 *   already holds the rows; only the display window changes (instant).
 * - WIDEN (overlapping coverage): fetches ONLY the missing date band(s), with the backend's
 *   overlap band mode so rows straddling a band boundary are included, and extends coverage.
 * - DISJOINT window / filters changed / forced (manual REFRESH) / post-mutation (coverage
 *   invalidated by the reducer wrapper): full reload of the window, coverage reset.
 */
export const getUserExtendedRegulars =
  (dateDTO, { force = false } = {}) =>
  async (dispatch, getState) => {
    const key = lookAheadCacheKey(dateDTO);
    const filterKey = lookAheadFilterKey(dateDTO);
    const win = { start: dateDTO.start, finish: dateDTO.finish };
    activeLookAheadKey = key;

    try {
      const coverage = getState()?.scheduleActivity?.regularsCoverage;
      if (!force && coverage && coverage.filterKey === filterKey) {
        if (windowInsideCoverage(win, coverage)) {
          // Narrowing (or same window): pure local re-window, zero network. No loading
          // marker here — narrowing completes synchronously, and the marker (complete:false)
          // used to strand the streaming flag/chip forever since nothing ever completed it.
          dispatch({
            type: SET_REGULARS_SCOPE,
            payload: { window: win, coverage },
          });
          return;
        }
        if (windowsOverlap(win, coverage)) {
          // Widening: fetch only the missing band(s). Scope first (it settles the streaming
          // flags), THEN the loading marker so the chip shows while the band streams.
          const newCoverage = { ...mergeCoverage(win, coverage), filterKey };
          dispatch({
            type: SET_REGULARS_SCOPE,
            payload: { window: win, coverage: newCoverage },
          });
          dispatch({
            type: GET_REGULARS_CHUNK,
            payload: { rows: [], replace: false, complete: false },
          });
          for (const bandWindow of missingBands(win, coverage)) {
            await streamLookAheadChunks(
              dispatch,
              {
                ...dateDTO,
                start: bandWindow.start,
                finish: bandWindow.finish,
              },
              key,
              { band: true, replaceFirst: false },
            );
            if (activeLookAheadKey !== key) return;
          }
          return;
        }
      }

      // Full reload (first load, disjoint window, filters changed, forced refresh).
      // Scope first (settles streaming flags), then the loading marker.
      dispatch({
        type: SET_REGULARS_SCOPE,
        payload: {
          window: win,
          coverage: { ...win, filterKey },
          resetPool: true,
        },
      });
      dispatch({
        type: GET_REGULARS_CHUNK,
        payload: { rows: [], replace: false, complete: false },
      });
      await streamLookAheadChunks(dispatch, dateDTO, key, {
        band: false,
        replaceFirst: true,
      });
    } catch (err) {
      if (logoutforSpecificErrorCodes(err)) {
        dispatch(logout());
      }
      dispatch(getRegularsError(err.message));
      // MLAP-FILTER DEC-5: a refused/failed request is never rendered as an empty result —
      // the pane renders a refusal with a stable reason code instead of a toast + empty grid.
      if (activeLookAheadKey === key) dispatch(lookAheadSaLoadFailed(err));
    }
  };

const lookAheadSaLoadFailed = (err) => ({
  type: LOOK_AHEAD_SA_LOAD_FAILED,
  payload: { code: MLAP_LOAD_FAILED, status: err?.response?.status ?? null },
});

export const addScheduleActivity =
  (scenarioId, activityId, shouldUpdateCustomColumns) => async (dispatch) => {
    dispatch(addScheduleActivityRequest());
    try {
      const result = await axios.post(
        `${PDA_PREFIX}/schedule-activity/create-from-gantt/${scenarioId}/${activityId}`,
      );
      if (shouldUpdateCustomColumns) {
        await dispatch(getScheduleActivityCustomColumnsByScenario(scenarioId));
      }
      dispatch(addScheduleActivitySuccess(result.data));
      toast.success("The task has been successfully duplicated");
      return result.data;
    } catch (err) {
      toast.error("Something went wrong");
      if (logoutforSpecificErrorCodes(err)) {
        dispatch(logout());
      }
      dispatch(addScheduleActivityError(err.message));
    }
  };

export const updateDetailActivity =
  (scheduleActivity, scenarioId) => async (dispatch) => {
    dispatch(updateDetailActivityRequest());
    try {
      const res = await axios.put(
        `${PDA_PREFIX}/schedule-activity/details/${scenarioId}`,
        scheduleActivity,
      );
      dispatch(updateDetailActivitySuccess(res.data));
    } catch (err) {
      if (logoutforSpecificErrorCodes(err)) {
        dispatch(logout());
      }
      // Surface the BE reason (e.g. R-L03 locked-work floor) — the edit popup is
      // already closed, so without a toast the rejection is invisible; the grid
      // refetch below discards the stale optimistic row.
      toast.warning(serverErrorText(err.response?.data, err.message));
      dispatch(updateDetailActivityError(err.message));
      dispatch(fetchGetScheduleActivities(scenarioId));
    }
  };

export const updateScheduleActivitiesWithUsage =
  (scheduleActivities, simulationsUsage, scenarioId) => async (dispatch) => {
    // dispatch(updateScheduleActivitiesWithUsageRequest());
    try {
      const request = scheduleActivities.map((activity) => {
        return {
          scheduleActivity: activity,
          usageJsonList: simulationsUsage.filter(
            (u) => +u.activityUxId === activity.uxId,
          ),
        };
      });
      const res = await axios.put(
        `${PDA_PREFIX}/schedule-activity/update/all/${scenarioId}/with-usages`,
        request,
      );
      dispatch(updateScheduleActivitiesWithUsageSuccess(res.data));
      dispatch(updateSimulationUsage());
      toast.success(i18n.t("common.changesSaved"));
    } catch (err) {
      if (logoutforSpecificErrorCodes(err)) {
        dispatch(logout());
      }
      dispatch(updateScheduleActivitiesWithUsageError(err.message));
      toast.error("Something went wrong");
    }
  };

export const resetBaseline = (scenarioId) => async (dispatch) => {
  // dispatch(updateScheduleActivitiesRequest());
  dispatch(setUpdatingStatus(true));
  try {
    const res = await axios.put(
      `${PDA_PREFIX}/time-metrics/reset-baseline/${scenarioId}`,
    );
    dispatch(updateScheduleActivitiesSuccess(res.data));
    dispatch(setUpdatingStatus(false));
    toast.success("Baseline reseted");
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch(updateScheduleActivitiesError(err.message));
    dispatch(setUpdatingStatus(false));
  }
};

/** The toast of a regeneration job that failed: the server's reason when it sent a string one. */
const jobFailureMessage = (frame) =>
  typeof frame?.error === "string" && frame.error
    ? i18n.t("generativeWorkspace.scheduleUpdateFailedWithMsg", { msg: frame.error })
    : i18n.t("generativeWorkspace.scheduleUpdateFailed");

/**
 * "Convert" (rolling wave) on a Gantt task: turn the activity into a detailed
 * scope, then regenerate the block schedule.
 *
 * The regeneration goes through the async (202) endpoint and waits for the
 * terminal frame on `/topic/scenario-update/{username}`: the synchronous
 * `PUT /update-block-schedule` runs the whole generation on the HTTP thread
 * and can outlive the reverse-proxy read timeout on large scenarios, which
 * left the Gantt masked with no result. Without a username we cannot bind the
 * per-user topic, so the sync call stays as the fallback.
 *
 * `gantt` is unmasked on every exit path, and `refreshButton` is the toolbar
 * widget (may be absent) whose element is clicked to reload the Gantt data.
 */
export const applyRollingWave =
  (dataToSend, scenarioId, gantt, refreshButton) => async (dispatch) => {
    // The Gantt goes with its page: a wait for the scenario lock stops then, silently.
    const signal = abortOnDestroy(gantt);
    const unmask = () => {
      if (!gantt.isDestroyed) gantt.unmask();
    };
    const finishSuccessfully = () => {
      // The async endpoint returns 202 with no schedule payload, so the
      // remaining-capacity overlay computed from the old synchronous response
      // is dropped rather than left stale — the refresh below reloads the data.
      dispatch(updateSimulationUsage());
      toast.success(i18n.t("common.changesSaved"));
      unmask();
      refreshButton?.currentElement?.click();
    };

    const fail = (err, fallbackMessage) => {
      unmask();
      const message = regenerationErrorMessage(err, fallbackMessage);
      if (!message) return;
      toast.warning(message);
      if (logoutforSpecificErrorCodes(err)) {
        dispatch(logout());
      }
      dispatch(updateScheduleActivitiesError(err?.message || fallbackMessage));
    };

    try {
      await axios.post(
        `${PDA_PREFIX}/schedule-activity/convert-regular`,
        dataToSend,
      );
    } catch (err) {
      fail(err, "Conversion failed. Please try again.");
      return;
    }

    const username = currentUsername();

    if (!username) {
      try {
        await putBlockSchedule(scenarioId, { signal });
        finishSuccessfully();
      } catch (err) {
        fail(err, i18n.t("generativeWorkspace.scheduleUpdateFailed"));
      }
      return;
    }

    return new Promise((resolve) => {
      let cleanup = () => {};
      const onAbort = () => {
        cleanup();
        resolve(false);
      };
      // The signal is the Gantt's, shared by its other actions: take the listener away on settle.
      const settle = (value) => {
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      };
      signal.addEventListener("abort", onAbort, { once: true });
      cleanup = awaitAsyncJob({
        topic: `/topic/scenario-update/${username}`,
        itemId: scenarioId,
        timeoutMs: 300000,
        // The wait for the scenario lock has its own budget: the run keeps the whole timeout.
        timeoutAfterPost: true,
        post: () =>
          submitWhenFree(
            scenarioId,
            () => axios.put(`${PDA_PREFIX}/scenario/${scenarioId}/update-block-schedule/async`),
            { signal },
          ),
        onCompleted: () => {
          finishSuccessfully();
          settle(true);
        },
        onFailed: (frame) => {
          unmask();
          toast.error(jobFailureMessage(frame));
          dispatch(updateScheduleActivitiesError(frame?.error || "unknown error"));
          settle(false);
        },
        onRequestError: (err) => {
          fail(err, i18n.t("generativeWorkspace.scheduleUpdateFailed"));
          settle(false);
        },
        onTimeout: () => {
          // The server may have finished without us hearing about it — refetch
          // so the Gantt reflects whatever was actually persisted.
          unmask();
          dispatch(fetchGetScenario(scenarioId));
          dispatch(fetchGetScheduleActivities(scenarioId));
          dispatch(updateScheduleActivitiesError("timeout"));
          toast.warning(i18n.t("scheduleRegeneration.stillRunning"));
          settle(false);
        },
      });
    });
  };

/**
 * True when `scenarioId` is still the scenario open in the store. A regeneration can settle
 * minutes after it started; by then the user may have switched scenario through the URL,
 * the breadcrumb or the back button, none of which cancels the follow. Its refetch would
 * empty the open scenario's Gantt and fill it with the old scenario's rows, and its toasts
 * would speak about a scenario no longer shown.
 */
const isOpenScenario = (getState, scenarioId) =>
  String(getState()?.scenario?.item?.id) === String(scenarioId);

/**
 * True when a scenario other than `scenarioId` is open. Nothing open, or one still loading, is
 * not another one.
 */
const isAnotherScenarioOpen = (getState, scenarioId) => {
  const openId = getState()?.scenario?.item?.id;
  return openId != null && String(openId) !== String(scenarioId);
};

/**
 * Fire and forget: compute-EVM is server state, the next GET recomputes it anyway, and an
 * unhandled rejection (401, 5xx) would only print "Uncaught (in promise)".
 */
const recomputeEVM = (scenarioId) =>
  axios.get(`${PDA_PREFIX}/scenario/compute-EVM/${scenarioId}`).catch(() => {});

/**
 * The remaining-capacity overlay carried by the sync `PUT /update-block-schedule` answer
 * (ScheduleActivityWithUsage[]).
 */
const simulationUsageOf = (res) => {
  const simulationData = { remainingCapacity: [] };
  (Array.isArray(res?.data) ? res.data : []).forEach((data) => {
    (data.remainingCapacity || []).forEach((capacity) => {
      capacity.resourceName = data.scheduleActivity?.resourceNames;
      simulationData.remainingCapacity.push(capacity);
    });
  });
  return simulationData;
};

/**
 * Regenerates the block schedule of `scenarioId` through the sync PUT (made safe against the
 * scenario lock). The refetch and the success toast only concern the scenario open in the
 * store. A failure is reported unless another scenario is open by then, where it would speak
 * about a scenario not shown; `reportErrors` reports it anyway, for a caller that regenerates a
 * scenario the user is not looking at yet (AIProjectImport, before it navigates to it). The
 * open view's error state is never touched for another scenario. `computeEVM` (default true) asks
 * for the EVM recompute afterwards. `signal` aborts the wait for
 * the lock silently.
 */
export const updateScopeSchedule =
  (scenarioId, { computeEVM = true, reportErrors = false, signal } = {}) =>
  async (dispatch, getState) => {
    //dispatch(updateScheduleActivitiesRequest());
    try {
      const res = await putBlockSchedule(scenarioId, { signal });
      if (!isOpenScenario(getState, scenarioId)) {
        // compute-EVM is server state of this scenario: it runs even when another one is open.
        if (computeEVM) recomputeEVM(scenarioId);
        return;
      }
      // The rows come from GET /schedule, not from this response: the PUT answers
      // ScheduleActivityWithUsage, which carries none of the root-level dimensions
      // ExtendedSAwithConflictsDTO computes (duration + labor/global cost
      // Actuals/Remaining/At completion, planned costs), so normalizing it wrote 0
      // into all of them — every Date of Review move blanked the cost Actuals of a
      // scenario that had some. The GET also claims the latest schedule request, so
      // an older one still in flight cannot land on top of it.
      await dispatch(fetchGetScheduleActivities(scenarioId));
      dispatch(updateSimulationUsage(simulationUsageOf(res)));
      toast.success(i18n.t("common.changesSaved"));
      if (computeEVM) recomputeEVM(scenarioId);
    } catch (err) {
      if (err?.aborted) return;
      if (logoutforSpecificErrorCodes(err)) {
        dispatch(logout());
      }
      const anotherOpen = isAnotherScenarioOpen(getState, scenarioId);
      if (anotherOpen && !reportErrors) return;
      if (err?.runStillInProgress && isOpenScenario(getState, scenarioId)) {
        // The server may finish without us hearing about it: show what is persisted, in place.
        dispatch(refetchGetScheduleActivities(scenarioId));
      }
      toast.warning(
        regenerationErrorMessage(err, i18n.t("generativeWorkspace.scheduleUpdateFailed")),
      );
      if (!anotherOpen) dispatch(updateScheduleActivitiesError(err.message));
    }
  };

/**
 * The warning of a regeneration still running when its follow gave up: it stays until closed,
 * the instruction in it ("reload in a few minutes") must not vanish in five seconds.
 */
const warnStillRunning = () => toast.warning(i18n.t("scheduleRegeneration.stillRunning"), { autoClose: false });

/**
 * The one end of every background regeneration (followScheduleUpdateJob,
 * regenerateScheduleInBackground): the "Updating usages" card is its only outcome message.
 * `outcome.kind`: "completed" (`simulation`: the remaining-capacity overlay of a sync answer,
 * else none) → card done, the open scenario refetched in place, EVM recomputed; "failed"
 * (`message`) → card failed; "aborted" (the scenario or the session went away) → card gone,
 * nothing else; "unknown" (timeout, another user's job, budget spent) → card gone, then on the
 * open scenario only a refetch and the sticky "still running" warning.
 * Resolves true on completion, false otherwise.
 */
const settleRegeneration = async (dispatch, getState, scenarioId, card, outcome) => {
  if (outcome.kind === "aborted") {
    card.dismiss();
    return false;
  }
  if (outcome.kind === "completed") {
    card.completed();
    if (isOpenScenario(getState, scenarioId)) {
      // The refetch reloads the data in place (fetchGetScheduleActivities would empty the rows
      // and remount the Gantt).
      dispatch(updateSimulationUsage(outcome.simulation));
      await dispatch(refetchGetScheduleActivities(scenarioId));
    }
    // Server state of this scenario: recomputed even when another one is open.
    recomputeEVM(scenarioId);
    return true;
  }
  if (outcome.kind === "failed") {
    card.failed(outcome.message);
    return false;
  }
  card.dismiss();
  if (!isOpenScenario(getState, scenarioId)) return false;
  // The server may have finished without us hearing about it.
  dispatch(refetchGetScheduleActivities(scenarioId));
  warnStillRunning();
  return false;
};

/**
 * The auto-feed cascade is server-triggered and runs each scenario (feed, then a regeneration
 * only when its Date of Review had to move): its per-scenario COMPLETED frame is the one
 * moment the usages of `scenarioId` are known to have changed. The scenario open in the
 * store is refetched in place, as settleRegeneration does; another scenario is left alone.
 * Resolves true when a refetch was dispatched.
 */
export const refreshOpenScenarioAfterAutoFeed = (scenarioId) => async (dispatch, getState) => {
  if (!isOpenScenario(getState, scenarioId)) return false;
  await dispatch(refetchGetScheduleActivities(scenarioId));
  return true;
};

/**
 * Follows a SCHEDULE_UPDATE job the backend already started (PDR-0141: a Date of Review change
 * regenerates the usages itself). Nothing is posted (see followScheduleJob). It runs in the
 * background: the "Updating usages" card shows it and is its only outcome message (done or
 * failed). A follow that cannot tell the outcome (timeout, another user's job) drops the card,
 * refetches and warns, on the open scenario only. Resolves true on completion, false otherwise.
 * Registered without the card's key: dismissing the card hides it, while the backend job runs
 * on and its end still refetches and recomputes EVM; a logout or a scenario switch stops it.
 */
export const followScheduleUpdateJob = (scenarioId, jobId, etaSeconds = null) => async (dispatch, getState) => {
  // The job's first frames (its ETA) left before the DoR response: the response carries it.
  const card = startRegenerationCard(dispatch, getState, scenarioId, jobId, undefined, { etaSeconds });
  const controller = registerScheduleFollow(scenarioId);
  let followed;
  try {
    followed = await followScheduleJob(scenarioId, jobId, {
      signal: controller.signal,
      onProgress: card.progress,
    });
  } finally {
    unregisterScheduleFollow(controller);
  }
  const outcome =
    followed === "completed" || followed === "aborted"
      ? { kind: followed }
      : followed === "failed"
        ? { kind: "failed", message: i18n.t("generativeWorkspace.scheduleUpdateFailed") }
        : { kind: "unknown" };
  return settleRegeneration(dispatch, getState, scenarioId, card, outcome);
};

/** What a refused regeneration request means for the background card. */
const requestErrorOutcome = (dispatch, err) => {
  if (err?.aborted) return { kind: "aborted" };
  if (logoutforSpecificErrorCodes(err)) dispatch(logout());
  if (err?.runStillInProgress) return { kind: "unknown" };
  return {
    kind: "failed",
    message: regenerationErrorMessage(err, i18n.t("generativeWorkspace.scheduleUpdateFailed")),
  };
};

/**
 * `PUT /update-block-schedule/async`, made safe against the scenario lock (submitWhenFree), then
 * followed to its end on the user's topic, like applyRollingWave. The backend registers the job
 * under the lock: the next waiter on the scenario gets its jobId and `startedBy`, and its
 * frames reach this card. `waitSignal` stops the wait for the lock (nothing submitted yet);
 * `followSignal` stops everything. Resolves a settleRegeneration outcome; never rejects.
 */
const runAsyncRegeneration = (
  dispatch,
  scenarioId,
  username,
  { idempotencyKey, waitSignal, followSignal, onWait, onAdmitted, onProgress },
) =>
  new Promise((resolve) => {
    if (followSignal.aborted) {
      resolve({ kind: "aborted" });
      return;
    }
    let cleanup = () => {};
    const onAbort = () => {
      cleanup();
      resolve({ kind: "aborted" });
    };
    const settle = (outcome) => {
      followSignal.removeEventListener("abort", onAbort);
      resolve(outcome);
    };
    followSignal.addEventListener("abort", onAbort, { once: true });
    cleanup = awaitAsyncJob({
      topic: `/topic/scenario-update/${username}`,
      itemId: scenarioId,
      timeoutMs: 300000,
      // The wait for the scenario lock has its own budget: the run keeps the whole timeout.
      timeoutAfterPost: true,
      onProgress,
      post: async () => {
        const response = await submitWhenFree(
          scenarioId,
          () =>
            axios.put(`${PDA_PREFIX}/scenario/${scenarioId}/update-block-schedule/async`, null, {
              headers: { "Idempotency-Key": idempotencyKey },
            }),
          { signal: waitSignal, onWait },
        );
        onAdmitted();
        return response;
      },
      onCompleted: () => settle({ kind: "completed" }),
      onFailed: (frame) => settle({ kind: "failed", message: jobFailureMessage(frame) }),
      onRequestError: (err) => settle(requestErrorOutcome(dispatch, err)),
      onTimeout: () => settle({ kind: "unknown" }),
    });
  });

let backgroundRegenerationSeq = 0;

/**
 * Regenerates the schedule in the background after a Date of Review the backend did not
 * regenerate itself (ALREADY_RUNNING: the running job predates the new date; NOT_STARTED: kill
 * switch off, older backend). Goes through the async endpoint (runAsyncRegeneration); the sync
 * PUT stays only for a session without a username, which cannot bind the per-user topic. Same
 * card and messages as followScheduleUpdateJob. While another run holds the scenario the card
 * says who started it. Dismissing the card cancels the wait for the lock only, before anything
 * is submitted; a logout or a scenario switch cancels the whole of it.
 * Resolves true on success, false otherwise; never rejects.
 */
export const regenerateScheduleInBackground = (scenarioId) => async (dispatch, getState) => {
  backgroundRegenerationSeq += 1;
  // Unique across page loads too: it is also the Idempotency-Key of the submission.
  const key = `schedule-regeneration-${scenarioId}-${Date.now()}-${backgroundRegenerationSeq}`;
  const card = startRegenerationCard(dispatch, getState, scenarioId, key);
  const wait = registerScheduleFollow(scenarioId, key);
  const follow = registerScheduleFollow(scenarioId);
  follow.signal.addEventListener("abort", () => wait.abort(), { once: true });
  let waited = false;
  const onWait = (_jobId, { startedBy } = {}) => {
    waited = true;
    card.waiting({ startedBy: startedBy ?? null });
  };
  const onAdmitted = () => {
    unregisterScheduleFollow(wait);
    if (waited) card.waiting(null);
  };
  let outcome;
  try {
    const username = currentUsername();
    if (username) {
      outcome = await runAsyncRegeneration(dispatch, scenarioId, username, {
        idempotencyKey: key,
        waitSignal: wait.signal,
        followSignal: follow.signal,
        onWait,
        onAdmitted,
        onProgress: card.progress,
      });
    } else {
      try {
        const res = await putBlockSchedule(scenarioId, { signal: wait.signal, onWait });
        onAdmitted();
        outcome = { kind: "completed", simulation: simulationUsageOf(res) };
      } catch (err) {
        outcome = requestErrorOutcome(dispatch, err);
      }
    }
  } finally {
    unregisterScheduleFollow(wait);
    unregisterScheduleFollow(follow);
  }
  return settleRegeneration(dispatch, getState, scenarioId, card, outcome);
};

// The Gantt's add dialogs also open inside the Generative Workspace, which
// shows the regenerated schedule itself: navigating to the preparation view
// would throw the user out of the workspace.
export const showScheduleAfterScopeImport = (navigate, projectId, scenarioId, structureId) => {
  if (window.location.pathname.startsWith("/generative-scheduling")) return;
  const view = sessionStorage.getItem("schedule-current-view") || "gantt";
  navigate(`/preparation/view/${view}/${projectId}/${scenarioId}/${structureId}`);
};

const authorizationHeader = () => axios.defaults?.headers?.common?.Authorization ?? null;

/**
 * The regeneration of a scope import whose dialog went away before it was sent (it was waiting
 * for a run holding the scenario): the scopes are already imported, so it still goes, through
 * the same lock-aware path, with no card, toast or refetch. It goes only with the credentials
 * the import was made with (`authorization`): a logout aborts it through the registry, and any
 * other session (logout, user or tenant switch: a new token) makes each submission refuse to
 * send. A scenario switch does not stop it. Resolves true once submitted, false otherwise.
 */
const submitDetachedRegeneration = async (scenarioId, authorization) => {
  const session = registerScheduleFollow(scenarioId, null, { untilLogout: true });
  const sameSession = () =>
    !session.signal.aborted && authorization !== null && authorizationHeader() === authorization;
  try {
    await submitWhenFree(
      scenarioId,
      () =>
        sameSession()
          ? axios.put(`${PDA_PREFIX}/scenario/${scenarioId}/update-block-schedule/async`)
          : Promise.reject(Object.assign(new Error("Session changed"), { aborted: true })),
      { signal: session.signal },
    );
    return true;
  } catch {
    return false;
  } finally {
    unregisterScheduleFollow(session);
  }
};

/**
 * Step-2 ("Define your inputs" → "Update schedule") of the Add structure /
 * Add scope dialogs. Regenerates the block schedule for the scenario.
 *
 * This used to hit the synchronous `PUT /update-block-schedule`, which runs
 * the full schedule generation on the HTTP thread and can outlive the
 * reverse-proxy read timeout on large scenarios — leaving the dialog's
 * "Update schedule" button spinning with no result. It now fires the async
 * variant (202) and awaits the terminal frame on
 * `/topic/scenario-update/{username}` (the COMPLETED frame is lightweight —
 * the callers refetch schedule/scenario over HTTP).
 *
 * Resolves `true` on success and `false` on failure so callers can decide
 * whether to close the modal, without an unhandled promise rejection.
 * `signal` (the dialog's page unmounting) stops the wait and the follow: `false`, no message.
 * A regeneration not sent yet by then (still waiting for the scenario lock) goes on detached
 * (submitDetachedRegeneration): the scopes are imported already and must not stay unscheduled.
 */
export const updateScopeScheduleForScopeImport =
  (projectId, scenarioId, structureId, navigate, { signal } = {}) => async (dispatch) => {
    dispatch(updateScheduleActivitiesImportScopeRequest());

    const username = currentUsername();

    // No username → can't bind the per-user topic. Fall back to the synchronous
    // endpoint, but surface any error instead of leaving the button spinning.
    if (!username) {
      try {
        await putBlockSchedule(scenarioId, { signal });
        dispatch(updateScheduleActivitiesImportScopeSuccess());
        toast.success(i18n.t("common.changesSaved"));
        showScheduleAfterScopeImport(navigate, projectId, scenarioId, structureId);
        return true;
      } catch (err) {
        if (err?.aborted) return false;
        toast.warning(regenerationErrorMessage(err, i18n.t("generativeWorkspace.scheduleUpdateFailed")));
        if (logoutforSpecificErrorCodes(err)) dispatch(logout());
        dispatch(updateScheduleActivitiesImportScopeError(err.message));
        return false;
      }
    }

    // awaitAsyncJob polls /async-job/{jobId}/status as a WS fallback, so a lost
    // terminal frame resolves within seconds instead of stranding the button
    // until the hard timeout.
    // The import's credentials: a detached regeneration never goes under another session.
    const authorization = authorizationHeader();
    // The latest PUT sent: on leaving the page, the regeneration counts as sent once it is accepted.
    let lastPut = null;
    let pageLeft = false;
    const onPageLeft = () => {
      if (pageLeft) return;
      pageLeft = true;
      const accepted = lastPut ? lastPut.then(() => true, () => false) : Promise.resolve(false);
      accepted.then((sent) => {
        if (!sent) submitDetachedRegeneration(scenarioId, authorization);
      });
    };

    return new Promise((resolve) => {
      // navigate() closes the dialog synchronously (useDialogSafeNavigate), which aborts `signal`:
      // the outcome must be settled before it, or the abort listener would turn a success into
      // `false` and the caller would skip the Gantt reload (RATIER FIGEAC C-21).
      let settled = false;
      let cleanup = () => {};
      const onAbort = () => {
        if (settled) return;
        settled = true;
        cleanup();
        onPageLeft();
        resolve(false);
      };
      const settle = (value) => {
        settled = true;
        signal?.removeEventListener("abort", onAbort);
        resolve(value);
      };
      cleanup = awaitAsyncJob({
        topic: `/topic/scenario-update/${username}`,
        itemId: scenarioId,
        timeoutMs: 300000,
        // The wait for the scenario lock has its own budget: the run keeps the whole timeout.
        timeoutAfterPost: true,
        // A run holding the scenario (409 RUN_IN_PROGRESS) is waited out, then the import's
        // regeneration is resubmitted: it must apply the scopes just imported.
        post: () =>
          submitWhenFree(
            scenarioId,
            () => {
              lastPut = axios.put(`${PDA_PREFIX}/scenario/${scenarioId}/update-block-schedule/async`);
              return lastPut;
            },
            { signal },
          ),
        onCompleted: () => {
          if (settled) return;
          settle(true);
          dispatch(updateScheduleActivitiesImportScopeSuccess());
          toast.success(i18n.t("common.changesSaved"));
          showScheduleAfterScopeImport(navigate, projectId, scenarioId, structureId);
        },
        onFailed: (frame) => {
          if (settled) return;
          settle(false);
          dispatch(
            updateScheduleActivitiesImportScopeError(frame?.error || "failed"),
          );
          toast.error(jobFailureMessage(frame));
        },
        onRequestError: (err) => {
          if (settled) return;
          if (err?.aborted) {
            // A signal already aborted when the wait started never fires its listener.
            if (signal?.aborted) onPageLeft();
            settle(false);
            return;
          }
          settle(false);
          if (logoutforSpecificErrorCodes(err)) dispatch(logout());
          dispatch(updateScheduleActivitiesImportScopeError(err.message));
          toast.error(regenerationErrorMessage(err, i18n.t("generativeWorkspace.scheduleUpdateFailed")));
        },
        onTimeout: () => {
          if (settled) return;
          settle(false);
          // The server may have finished without us hearing about it — refetch
          // so the Gantt reflects whatever was actually persisted.
          dispatch(fetchGetScenario(scenarioId));
          reloadGanttAfterImport(dispatch, scenarioId);
          dispatch(updateScheduleActivitiesImportScopeError("timeout"));
          toast.warning(i18n.t("scheduleRegeneration.stillRunning"));
        },
      });
      // The dialog's page went away: stop listening, say nothing, navigate nowhere.
      signal?.addEventListener("abort", onAbort, { once: true });
    });
  };

export const clearScheduleActivities = () => async (dispatch) => {
  dispatch({
    type: CLEAR_SCHEDULE_ACTIVITIES,
    payload: [],
  });
};

export const getAllPortfolioDashboard = () => async (dispatch) => {
  dispatch(updateScheduleActivitiesRequest());
  try {
    const res = await axios.get(
      `${PDA_PREFIX}/schedule-activity/all-portfolio-dashboard`,
    );
    dispatch(updateScheduleActivitiesSuccess(res.data));
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch(updateScheduleActivitiesError(err.message));
  }
};

/**
 * Action to generate a schedule.
 * @param {Id of the current scenario} scenarioId
 * @param {*} navigate
 * @returns
 */
export const generateSchedule = (scenarioId, navigate) => async (dispatch) => {
  dispatch(getScheduleActivitiesRequest(scenarioId));
  try {
    const res = await axios.get(
      `${PDA_PREFIX}/scenario/${scenarioId}/generate-schedule`,
    );
    dispatch(getScheduleActivitiesSuccess(res.data));
    navigate(`/preparation`);
    toast.success("Schedule has been successfully generated!");
  } catch (err) {
    dispatch(getScheduleActivitiesError(err.message));
    // navigate(`/preparation`);
    toast.error(
      "Nothing to worry about, issue is easy to fix! Contact support, they will help you in a few minutes...",
    );
  }
};

/**
 *
 * @param {Id of the current scenario} scenarioId
 * @returns
 */
export const updateSchedule = (scenarioId) => async (dispatch) => {
  // dispatch(updateScheduleRequest());
  try {
    const res = await axios.get(
      `${PDA_PREFIX}/scenario/${scenarioId}/update-schedule`,
    );

    const simulationData = {
      simulationUsage: [],
      remainingCapacity: [],
      simulatedCapacity: [],
    };
    res.data.forEach((data) => {
      for (let i = 0; i < data.usageJsonList.length; i++) {
        data.usageJsonList[i].resourceName =
          data.scheduleActivity.resourceNames;
        simulationData.simulationUsage.push(data.usageJsonList[i]);
      }
      for (let i = 0; i < data.remainingCapacity.length; i++) {
        data.remainingCapacity[i].resourceName =
          data.scheduleActivity.resourceNames;
        simulationData.remainingCapacity.push(data.remainingCapacity[i]);
      }
      for (let i = 0; i < data.simulatedCapacity.length; i++) {
        data.simulatedCapacity[i].resourceName =
          data.scheduleActivity.resourceNames;
        simulationData.simulatedCapacity.push(data.simulatedCapacity[i]);
      }
    });
    // Same as updateScopeSchedule: the reducer needs the full DTO to keep the
    // root-level dimensions.
    dispatch(updateScheduleActivitiesSuccess(res.data));
    dispatch(updateSimulationUsage(simulationData));
    toast.success("Simulation successfully calculated!");
    return Promise.resolve(res.data);
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch(updateScheduleActivitiesError(err.message));
    toast.warning(serverErrorText(err.response?.data, err.message));
    return Promise.reject();
  }
};

export const updateInterProjectSchedule = () => async (dispatch) => {
  dispatch(simulationProcess());
  try {
    const portfolioActivitiesWithUsages = await axios.get(
      `${PDA_PREFIX}/scenario/simulate-on-portfolio`,
    );
    const simulationData = {
      simulationUsage: [],
      remainingCapacity: [],
      simulatedCapacity: [],
      scenarioUsage: portfolioActivitiesWithUsages.data,
    };

    simulationData.simulationUsage = portfolioActivitiesWithUsages.data.flatMap(
      (val) => {
        const { projectName, scenarioName, usageJsonList } = val;
        return usageJsonList.map((value) => ({
          ...value,
          scenarioName,
          projectName,
        }));
      },
    );

    simulationData.remainingCapacity =
      portfolioActivitiesWithUsages.data.flatMap(
        (val) => val.remainingCapacity,
      );
    simulationData.simulatedCapacity =
      portfolioActivitiesWithUsages.data.flatMap(
        (val) => val.simulatedCapacity,
      );
    dispatch(simulationSuccess(simulationData));
    return Promise.resolve(portfolioActivitiesWithUsages.data);
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    const serverMsg = serverErrorText(err.response?.data, err.message);
    dispatch(simulationError(serverMsg));
    toast.warning(serverMsg);
    return Promise.reject(serverMsg);
  }
};

export const simulateMultipleProjects =
  (portfolioScenarioId) => async (dispatch) => {
    dispatch(simulationProcess());
    try {
      const res = await axios.post(
        `${PDA_PREFIX}/project/simulate/multiple-projects/${portfolioScenarioId}`,
      );
      const simulationData = {
        simulationUsage: [],
        remainingCapacity: [],
        simulatedCapacity: [],
        scenarioUsage: res.data,
      };

      simulationData.simulationUsage = (res.data || []).flatMap((val) => {
        const { projectName, scenarioName, usageJsonList } = val;
        return (usageJsonList || []).map((value) => ({
          ...value,
          scenarioName,
          projectName,
        }));
      });

      simulationData.remainingCapacity = (res.data || []).flatMap(
        (val) => val.remainingCapacity || [],
      );
      simulationData.simulatedCapacity = (res.data || []).flatMap(
        (val) => val.simulatedCapacity || [],
      );
      dispatch(simulationSuccess(simulationData));
      return Promise.resolve(res.data);
    } catch (err) {
      if (logoutforSpecificErrorCodes(err)) {
        dispatch(logout());
      }
      const serverMsg = serverErrorText(err.response?.data, err.message);
      dispatch(simulationError(serverMsg));
      toast.warning(serverMsg);
      return Promise.reject(serverMsg);
    }
  };

export const simulateSchedule = (id) => async (dispatch) => {
  // dispatch(updateScheduleActivitiesRequest());
  dispatch(setUpdatingStatus(true));
  try {
    const res = await axios.get(
      `${PDA_PREFIX}/scenario/${id}/simulate-schedule`,
    );
    dispatch(updateScheduleActivitiesSuccess(res.data));
    dispatch(setUpdatingStatus(false));
    toast.success("Simulation has been done. Click on save to finalize.");
  } catch (err) {
    dispatch(updateScheduleActivitiesError(err.message));
    dispatch(setUpdatingStatus(false));

    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    toast.error(
      "Nothing to worry about, issue is easy to fix! Contact support, they will help you in a few minutes...",
    );
  }
};

export const deleteScheduleActivity =
  (scheduleActivityId) => async (dispatch) => {
    await axios.delete(
      `${PDA_PREFIX}/schedule-activity/delete/${scheduleActivityId}`,
    );
  };

export const addManualScheduleActivity = (scenarioId) => async (dispatch) => {
  dispatch(addScheduleActivityRequest());
  try {
    const result = await axios.post(
      `${PDA_PREFIX}/schedule-activity/${scenarioId}`,
    );
    dispatch(addScheduleActivitySuccess(result.data));
    return result.data.id;
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch(addScheduleActivityError(err.message));
    return null;
  }
};

export const addManualSubtask = (scenarioId, parentId) => async (dispatch) => {
  dispatch(addScheduleActivityRequest());
  try {
    const res = await axios.post(
      `${PDA_PREFIX}/schedule-activity/${scenarioId}/add-subtask`,
      { sourceId: parentId },
    );
    dispatch(addSubtaskSuccess(res.data));
    return res.data.child.id;
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch(addScheduleActivityError(err.message));
    return null;
  }
};

export const addSummary =
  (scenarioId, parentId, amountOfRegularsToCreate = 1) =>
  async (dispatch) => {
    dispatch(addScheduleActivityRequest());
    try {
      const res = await axios.post(
        `${PDA_PREFIX}/schedule-activity/${scenarioId}/add-summary/${amountOfRegularsToCreate}`,
        { sourceId: parentId },
      );
      dispatch(addSummarySuccess(res.data));
      return res.data;
    } catch (err) {
      if (logoutforSpecificErrorCodes(err)) {
        dispatch(logout());
      }
      dispatch(addScheduleActivityError(err.message));
      return null;
    }
  };

export const deleteSingleScheduleActivity =
  (id, lineType) => async (dispatch) => {
    try {
      await axios.delete(
        `${PDA_PREFIX}/schedule-activity/single-delete/${id}/${lineType}`,
      );
      dispatch(deleteScheduleActivitySuccess(id));
      toast.success("Activity successfully deleted.");
      return true;
    } catch (err) {
      if (logoutforSpecificErrorCodes(err)) {
        dispatch(logout());
      } else {
        toast.error("Something went wrong");
      }
      return false;
      // toast.warn(err.response.data);
    }
  };

export const deleteScheduleActivitiesFromGantt =
  (batchData) => async (dispatch) => {
    try {
      await axios.post(
        `${PDA_PREFIX}/schedule-activity/batch-delete`,
        batchData,
      );
      dispatch(
        deleteScheduleActivitiesSuccess(batchData.map((d) => d.sourceId)),
      );
      toast.success("Activities successfully deleted.");
      return true;
    } catch (err) {
      if (logoutforSpecificErrorCodes(err)) {
        dispatch(logout());
      } else {
        toast.error("Something went wrong");
      }
      return false;
      // toast.warn(err.response.data);
    }
  };

export const updateFromDialogOnGantt = (data) => async (dispatch) => {
  try {
    axios.post(`${PDA_PREFIX}/schedule-activity/update-tasks`, data);
    toast.success("Activity successfully updated.");
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    toast.warn(serverErrorText(err.response?.data, err.message));
  }
};

// The rows a patch payload speaks for. Exported because the Gantt has to know
// them BEFORE the request settles — it marks those rows as having a write in
// flight so a resync triggered by another row cannot rewrite them from a stale
// snapshot. Deriving them twice, once here and once at each call site, is how
// the two drift apart.
export const collectPatchRowIds = (data, childrenIds = []) => {
  let ids = [];
  if (Array.isArray(data)) {
    ids = data.map((item) => item.sourceId);
  } else if (data) {
    // The same row is named differently across these endpoints.
    ids = [
      data.sourceId,
      data.targetId,
      data.saId,
      data.from?.id,
      data.to?.id,
    ].filter((v) => v);
  }
  return [...new Set([...childrenIds, ...ids])];
};

// Test hook on the message a refused Gantt edit shows (the toast body is otherwise
// a bare string, which e2e may not select by text).
export const REJECTED_EDIT_MESSAGE_TESTID = "gantt-edit-rejected-message";
const rejectedEditMessage = (content) => (
  <span data-testid={REJECTED_EDIT_MESSAGE_TESTID}>{content}</span>
);

export const patchScheduleActivities =
  (endpoint, data, childrenIds = []) =>
  async (dispatch) => {
    try {
      await axios.patch(`${PDA_PREFIX}/schedule-activity/${endpoint}`, data);
      emitPenaltiesChanged();

      return collectPatchRowIds(data, childrenIds);
    } catch (err) {
      if (logoutforSpecificErrorCodes(err)) {
        dispatch(logout());
      }
      // A 4xx carries an actionable message (e.g. the R-L03 locked-work floor) —
      // surface it and rethrow so the Gantt can revert the optimistic cell edit.
      const status = err.response?.status;
      if (status >= 400 && status < 500 && err.response?.data) {
        toast.warn(rejectedEditMessage(serverErrorText(err.response.data, i18n.t("ganttContext.editRefused"))));
        throw err;
      }
      toast.error(rejectedEditMessage("Something went wrong when doing the update."));
      // Reject, always. Resolving a 5xx made every caller count the write as done:
      // the cell kept rendering a value the server never stored, and only a full
      // reload revealed it. Callers revert their optimistic edit from this rejection,
      // and updateItemsByIds awaits the queue with allSettled so one failed write
      // never cancels the resync of the rows that did save.
      throw err;
    }
  };

export const indentOrOutdentScheduleActivity =
  (data, action = "indent") =>
  async (dispatch) => {
    try {
      await axios.post(`${PDA_PREFIX}/schedule-activity/${action}`, data);
      return Promise.resolve();
    } catch (err) {
      if (logoutforSpecificErrorCodes(err)) {
        dispatch(logout());
      }
      toast.error(`Something went wrong when doing the ${action}`);
      return Promise.reject();
    }
  };

const collectScheduleActivitiesByIdsResponse = (data, sink) => {
  if (!data) return;
  if (Array.isArray(data)) {
    sink.scheduleActivities.push(...data);
  } else if (typeof data === "object") {
    sink.scheduleActivities.push(...(data.scheduleActivities || []));
    sink.resourceTasks.push(...(data.resourceTasks || []));
    sink.hasResourceTasks = true;
  }
  // A non-array, non-object 200 body (e.g. an HTML proxy/error page) is not a
  // valid payload — apply nothing rather than mis-reading it as an object.
};

// Run `worker` over `items` with at most `limit` in flight, so a per-id
// recovery pass over a large batch can't fan a single failure into a storm.
const mapWithConcurrency = async (items, limit, worker) => {
  let index = 0;
  const runNext = async () => {
    while (index < items.length) {
      const current = index;
      index += 1;
      await worker(items[current]);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, runNext),
  );
};

export const getScheduleActivitiesByIds =
  (scenarioId, ids) => async (dispatch) => {
    // isNaN(false) and isNaN(null) are false: without the type checks a refused write's `false`
    // went out as an id and the backend answered 500 (Virbac B5).
    const validIds = (ids || []).filter(
      (item) => item != null && typeof item !== "boolean" && item !== "" && !isNaN(item)
    );
    if (!validIds.length) return;
    const url = `${PDA_PREFIX}/schedule-activity/${scenarioId}/ids`;
    const requestId = currentScheduleSequence(scenarioId);
    const isLatestByIds = claimByIdsRequest(scenarioId);
    const flush = (sink) => {
      // A newer by-ids refresh for this scenario has already answered: applying
      // this older snapshot on top of it would undo the fresher rows.
      if (!isLatestByIds()) return;
      dispatch(
        updateScheduleActivitiesByIds(sink.scheduleActivities, requestId),
      );
      if (sink.hasResourceTasks) {
        dispatch(topDownResourceTaskUpdate(sink.resourceTasks));
      }
    };
    const surface = (err) => {
      if (logoutforSpecificErrorCodes(err)) {
        dispatch(logout());
      }
      console.error(err);
      toast.error("Something went wrong. Reload the page to sync the data.");
    };
    try {
      const res = await axios.post(url, validIds);
      const sink = {
        scheduleActivities: [],
        resourceTasks: [],
        hasResourceTasks: false,
      };
      collectScheduleActivitiesByIdsResponse(res.data, sink);
      flush(sink);
    } catch (err) {
      // The date edit itself already committed and the grid reflects it; this
      // call only re-pulls the server-reconciled values for the touched records.
      // Only a plain 500 on a multi-id batch is worth splitting: that's the "one
      // row the server can't serialize" signature. A network outage (no
      // response), a 4xx (an expired session must log out NOW, not after N doomed
      // retries), a 502/503/504 (gateway/outage — every id would fail, so
      // splitting is a retry storm), or a single-id request can't be salvaged, so
      // surface those immediately.
      const status = err.response?.status;
      if (status !== 500 || validIds.length <= 1) return surface(err);
      // One unservable/locked row 500s the whole batch. The old code discarded
      // every other record's update and popped a "reload the page" toast even
      // though the edit succeeded. Re-fetch the ids individually (bounded
      // concurrency so a big batch can't storm an already-struggling backend) so
      // the healthy rows still sync, and only alarm the user when nothing comes
      // back at all.
      const sink = {
        scheduleActivities: [],
        resourceTasks: [],
        hasResourceTasks: false,
      };
      const failed = [];
      await mapWithConcurrency(validIds, 6, async (id) => {
        try {
          const res = await axios.post(url, [id]);
          collectScheduleActivitiesByIdsResponse(res.data, sink);
        } catch {
          failed.push(id);
        }
      });
      if (!sink.scheduleActivities.length && !sink.resourceTasks.length) {
        return surface(err);
      }
      if (failed.length) {
        console.warn(
          `[getScheduleActivitiesByIds] ${failed.length} record(s) could not be re-synced and were left on their optimistic values:`,
          failed,
        );
      }
      flush(sink);
    }
  };

export const uploadMpp = (file, gantt, scenarioId) => async (dispatch) => {
  dispatch(uploadMppRequest());
  const formData = new FormData();
  formData.append("file", file, file.name);
  gantt && gantt.maskBody("Importing project ...");
  try {
    const res = await axios.post(
      `${PDA_PREFIX}/schedule-activity/upload-mpp/${scenarioId}`,
      formData,
    );
    dispatch(fetchGetScenario(scenarioId));
    dispatch(getResourcesByScenarioId(scenarioId));
    dispatch(uploadMppSuccess(res.data));
    gantt && gantt.unmaskBody();
    toast.success("Import done successfully !");
    return Promise.resolve(res.data);
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch(uploadMppError(err));
    gantt && gantt.unmaskBody();
    toast.error(`Import failed`);
    return Promise.reject();
  }
};

export const uploadBatchMpp = (files) => async (dispatch) => {
  dispatch(uploadMppRequest());
  const formData = new FormData();
  files.forEach((file, index) => {
    formData.append(file.name, file, file.name);
  });

  try {
    const res = await axios.post(
      `${PDA_PREFIX}/schedule-activity/upload-batch-mpp`,
      formData,
    );
    toast.success("Import done successfully!");
    return Promise.resolve(res.data);
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch(uploadMppError(err));
    toast.error(serverErrorText(err.response?.data, "Import failed"));
    return Promise.reject();
  }
};

export const saveToLibrary =
  (ganttRef, scenarioId, data, libraryId) => async (dispatch) => {
    ganttRef.current.ganttInstance.maskBody("Saving tasks to library...");
    try {
      await axios.post(
        `${PDA_PREFIX}/schedule-activity/${scenarioId}/save-to-library/${libraryId}`,
        data,
      );
      ganttRef.current.ganttInstance.unmaskBody();
      toast.success("Selected tasks have been saved to library !");
      window.open("/knowledge/libraries", "_blank");
    } catch (err) {
      if (logoutforSpecificErrorCodes(err)) {
        dispatch(logout());
      }
      ganttRef.current.ganttInstance.unmaskBody();
      toast.error("Something went wrong during the saving.");
    }
  };

export const resetBaselineAtActivityLevel =
  (data, gantt) => async (dispatch) => {
    gantt.maskBody("Resetting baseline...");
    try {
      const res = await axios.patch(
        `${PDA_PREFIX}/schedule-activity/reset-baseline-at-activity-level`,
        data,
      );
      dispatch(resetBaselineOnActivity(res.data));
      toast.success("Baseline successfully reset !");
      gantt.unmaskBody();
    } catch (err) {
      gantt.unmaskBody();
      toast.error("Something went wrong during the reset");
    }
  };

export const resetBaselineAtActivityLevelOnSummary =
  (data, gantt) => async (dispatch) => {
    gantt.maskBody("Resetting baseline...");
    try {
      const res = await axios.patch(
        `${PDA_PREFIX}/schedule-activity/reset-baseline-at-activity-level-on-summary`,
        data,
      );
      dispatch(resetBaselineOnSummary(res.data));
      toast.success("Baseline successfully reset !");
      gantt.unmaskBody();
    } catch (err) {
      gantt.unmaskBody();
      toast.error("Something went wrong during the reset");
    }
  };

export const changeHierarchy = (data, scenarioId) => async (dispatch) => {
  try {
    await axios.patch(
      `${PDA_PREFIX}/schedule-activity/change-hierarchy/${scenarioId}`,
      data,
    );
    toast.success("Changes done successfully !");
  } catch (err) {
    toast.error("Something went wrong during the hierarchy change.");
  }
};
export const duplicateActivity = (idOfTaskToDuplicate) => async (dispatch) => {
  dispatch(addScheduleActivityRequest());
  try {
    const res = await axios.post(
      `${PDA_PREFIX}/schedule-activity/duplicate-activity`,
      { sourceId: idOfTaskToDuplicate },
    );
    dispatch(duplicateActivitySuccess(res.data));
    return res.data;
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch(addScheduleActivityError(err.message));
    return null;
  }
};

export const saveOnPortfolioSimulation = (simulationData) => async () => {
  try {
    await axios.post(
      `${PDA_PREFIX}/scenario/save-portfolio-simulation`,
      simulationData,
    );
    toast.success("Save successfully done !");
    return Promise.resolve();
  } catch (err) {
    toast.error("Something went wrong during save. Please contact support");
    return Promise.reject();
  }
};

export const importFile = (file, scenarioId) => async (dispatch) => {
  try {
    const formData = new FormData();
    formData.append("file", file, file.name);
    const url = `${PDA_PREFIX}/schedule-activity/upload-mpp/${scenarioId}`;
    const resp = await axios.post(url, formData);
    return resp.data;
  } catch (err) {
    toast.error("Something went wrong");
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    return;
  }
};

export const applyChangesFromImportFile =
  (file, rules, scenarioId) => async (dispatch) => {
    try {
      const formData = new FormData();
      formData.append("file", file, file.name);
      formData.append("rules", JSON.stringify(rules));
      const url = `${PDA_PREFIX}/schedule-activity/upload-mpp/apply-rules/${scenarioId}`;
      const res = await axios.post(url, formData);
      return {
        activities: res.data,
        mergedLinks: mergedLinksFromHeaders(res.headers),
      };
    } catch (err) {
      toast.error("Something went wrong");
      if (logoutforSpecificErrorCodes(err)) {
        dispatch(logout());
      }
      return;
    }
  };

export const saveSummaryReport = (scenarioId) => async (dispatch) => {
  const response = await dispatch(downloadSummaryReport(scenarioId));
  if (!response?.data) return;
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = "summary-report.xlsx";
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
};

export const downloadSummaryReport = (scenarioId) => async (dispatch) => {
  try {
    const fileToDownload = await axios.get(
      `${PDA_PREFIX}/schedule-activity/upload-mpp/${scenarioId}/summary-report`,
      { responseType: "blob" },
    );
    return fileToDownload;
  } catch (err) {
    toast.error(
      "Something went wrong during summary report downloading. Please contact support",
    );
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    return null;
  }
};

export const getScheduleActivityCustomColumnsByScenario =
  (scenarioId) => async (dispatch) => {
    dispatch(getScheduleActivityCustomColumnsRequest());
    try {
      const res = await axios.get(
        `${PDA_PREFIX}/schedule-activity/custom-column/${scenarioId}`,
      );
      dispatch(getScheduleActivityCustomColumnsSuccess(res.data));
    } catch (err) {
      if (logoutforSpecificErrorCodes(err)) {
        dispatch(logout());
      }
      dispatch(getScheduleActivityCustomColumnsError(err.message));
      toast.error("Something went wrong");
    }
  };

export const getScheduleActivityCustomColumns =
  (silent = false) =>
  async (dispatch) => {
    !silent && dispatch(getScheduleActivityCustomColumnsRequest());
    try {
      const res = await axios.get(
        `${PDA_PREFIX}/schedule-activity/custom-column`,
      );
      dispatch(getScheduleActivityCustomColumnsSuccess(res.data));
    } catch (err) {
      if (logoutforSpecificErrorCodes(err)) {
        dispatch(logout());
      }
      dispatch(getScheduleActivityCustomColumnsError(err.message));
      toast.error("Something went wrong");
    }
  };

export const getScheduleActivityCustomColumnsOfApprovedProjects =
  (start, finish) => async (dispatch) => {
    const formData = new FormData();
    if (start && finish) {
      formData.append("start", start);
      formData.append("finish", finish);
    }
    dispatch(getScheduleActivityCustomColumnsRequest());
    try {
      const res = await axios.post(
        `${PDA_PREFIX}/schedule-activity/custom-column/of-approved-projects`,
        formData,
      );
      dispatch(getScheduleActivityCustomColumnsSuccess(res.data));
    } catch (err) {
      if (logoutforSpecificErrorCodes(err)) {
        dispatch(logout());
      }
      dispatch(getScheduleActivityCustomColumnsError(err.message));
      toast.error("Something went wrong");
    }
  };

export const renameScheduleActivityCustomColumn =
  (columnId, newColumnName) => async (dispatch) => {
    try {
      await axios.put(
        `${PDA_PREFIX}/schedule-activity/custom-column-name/${columnId}`,
        {},
        { params: { newColumnName } },
      );
    } catch (err) {
      if (logoutforSpecificErrorCodes(err)) {
        dispatch(logout());
      }
      toast.error("Something went wrong");
    }
  };

export const editScheduleActivityCustomColumn =
  ({ scheduleActivityId, columnNameId, columnValue }) =>
  async (dispatch) => {
    try {
      const formData = new FormData();
      formData.append("columnNameId", columnNameId);
      formData.append("columnValue", columnValue);
      await axios.put(
        `${PDA_PREFIX}/schedule-activity/custom-column/${scheduleActivityId}`,
        formData,
      );
      dispatch(updateScheduleActivityCustomColumn(scheduleActivityId));
    } catch (err) {
      if (logoutforSpecificErrorCodes(err)) {
        dispatch(logout());
      }
      // Surface the backend's reason rather than the generic fallback. A 400
      // from the schedule-activity custom-column endpoint (once the spec 3.5
      // fail-safe lands) means the value was rejected — typically because
      // it's not in the configured DROPDOWN options. Telling the user why
      // beats a silent revert.
      const status = err.response?.status;
      const body = err.response?.data;
      const reason =
        (body && typeof body === "string" && body) ||
        body?.message ||
        (status === 400 ? "Invalid value for this column." : null) ||
        "Something went wrong";
      toast.error(reason);
    }
  };

const updateScheduleActivityCustomColumn =
  (scheduleActivityId) => async (dispatch) => {
    try {
      const { data } = await axios.get(
        `${PDA_PREFIX}/schedule-activity/custom-column/value/${scheduleActivityId}`,
      );
      const payload = { scheduleActivityId, data };
      dispatch({ type: UPDATE_SCHEDULE_ACTIVITY_CUSTOM_COLUMN, payload });
    } catch (err) {
      console.error("There was an error when updating custom column");
    }
  };

export const getScheduleActivityCustomColumnNames = () => async (dispatch) => {
  dispatch(getScheduleActivityCustomColumnNamesRequest());
  try {
    const res = await axios.get(
      `${PDA_PREFIX}/schedule-activity/custom-column-name`,
    );
    dispatch(getScheduleActivityCustomColumnNamesSuccess(res.data));
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    dispatch(getScheduleActivityCustomColumnNamesError(err.message));
    toast.error("Something went wrong");
  }
};

export const addBlockActivityOnAutomaticGantt =
  (scenarioId, gantt, parentRecord) => async (dispatch) => {
    try {
      // Armed before the request: the store is mutated again by the sequence
      // realignment below, and the reveal must already be allowed by then.
      armRevealIntent({ scenarioId });
      const { data } = await axios.patch(
        `${PDA_PREFIX}/schedule-activity/add-block-activity/${scenarioId}/${parentRecord.id}`,
      );
      dispatch(
        addScheduleActivitySuccess(data.created[0], {
          taskStore: gantt.taskStore,
          scenarioId,
          updatedItems: data.updated,
        }),
      );
      addMissingDependenciesToGantt(gantt, [...data.created, ...data.updated]);
      await realignSequenceOnGantt(gantt.taskStore, scenarioId, dispatch);
      toast.success(i18n.t("ganttContext.activityAdded"));
    } catch (err) {
      toast.error(i18n.t("ganttContext.activityAddError"));
    }
  };

export const addBlockMilestoneOnAutomaticGantt =
  (scenarioId, gantt, parentRecord) => async (dispatch) => {
    try {
      armRevealIntent({ scenarioId });
      const { data } = await axios.patch(
        `${PDA_PREFIX}/schedule-activity/add-block-milestone/${scenarioId}/${parentRecord.id}`,
      );
      dispatch(
        addScheduleActivitySuccess(data.created[0], {
          taskStore: gantt.taskStore,
          scenarioId,
          updatedItems: data.updated,
        }),
      );
      addMissingDependenciesToGantt(gantt, [...data.created, ...data.updated]);
      await realignSequenceOnGantt(gantt.taskStore, scenarioId, dispatch);
      toast.success(i18n.t("ganttContext.milestoneAdded"));
    } catch (err) {
      toast.error(i18n.t("ganttContext.milestoneAddError"));
    }
  };

export const addBlockOnAutomaticGantt =
  (scenarioId, gantt, parentRecord, amount = 2, shouldSequence = false) =>
  async (dispatch) => {
    try {
      armRevealIntent({ scenarioId });
      const { data } = await axios.patch(
        `${PDA_PREFIX}/schedule-activity/add-block/${amount}/${shouldSequence}/${scenarioId}/${parentRecord.id}`,
      );
      dispatch({
        type: ADD_SCHEDULE_ACTIVITIES,
        payload: data,
      });
      addMissingDependenciesToGantt(gantt, [...data.created, ...data.updated]);
      await realignSequenceOnGantt(gantt.taskStore, scenarioId, dispatch);
      toast.success(
        i18n.t(
          shouldSequence
            ? "ganttContext.blockAddedSequenced"
            : "ganttContext.blockAdded",
          { amount },
        ),
      );
    } catch (err) {
      toast.error(i18n.t("ganttContext.blockAddError"));
    }
  };

export const updateScheduleActivityFinishDateWithDuration =
  (data) => async (dispatch) => {
    try {
      const res = await axios.patch(
        `${PDA_PREFIX}/schedule-activity/finish-date`,
        data,
      );
      dispatch(updateScheduleActivityFinishDateWithDurationSuccess(res.data));
    } catch (err) {
      if (logoutforSpecificErrorCodes(err)) {
        dispatch(logout());
      }
      toast.error("Something went wrong when doing the update.");
    }
  };

export const changeTimelineIndexofEvent = (data) => async (dispatch) => {
  try {
    const res = await axios.patch(
      `${PDA_PREFIX}/schedule-activity/timeline-index`,
      data,
    );
    return res.data;
  } catch (err) {
    if (logoutforSpecificErrorCodes(err)) {
      dispatch(logout());
    }
    toast.error("Something went wrong when doing the update.");
  }
};
